import { Component, inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { App, AppState, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular';
import { I18nService, ThemeService } from '@nova-portal/frontend-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
	imports: [IonApp, IonRouterOutlet]
})
export class AppComponent implements OnDestroy {
	public swipeGesturesEnabled = true;

	private platform = inject(Platform);
	private router = inject(Router);
	private i18nService = inject(I18nService);

	private destruction = new Subject<void>();

	constructor() {
		const themeService = inject(ThemeService);

		if (environment.clientLogEnabled) {
			console.log(
				`isNative: ${Capacitor.isNativePlatform() ? 'yes' : 'no'} (${Capacitor.getPlatform()}) - Platform: ${
					this.platform.is('ios') ? 'ios' : this.platform.is('android') ? 'android' : 'desktop?'
				}`
			);
		}
		this.swipeGesturesEnabled = Capacitor.isNativePlatform();

		// Switch to Dark Mode, if enabled
		themeService.themeMode
			.pipe(takeUntil(this.destruction))
			.subscribe(mode => themeService.switchThemeMode(mode === 'dark'));

		this.initializeApp();
	}

	public ngOnDestroy(): void {
		this.destruction.next();
		this.destruction.complete();
	}

	private initializeApp(): void {
		this.platform.ready().then(() => {
			SplashScreen.hide();
			if (Capacitor.isNativePlatform()) {
				StatusBar.setStyle({ style: Style.Dark });
				if (this.platform.is('android')) {
					StatusBar.setOverlaysWebView({ overlay: true });

					// Fix Capacitor bug when web view overlay is enabled. On Android the keyboard then overlaps the
					// bottom inputs.
					this.handleKeyboardToggle();
				}

				// detect if the application is foreground or background
				this.runAppStateChangeListener();
			}

			// Safe area is not set correctly by Ionic / Capacitor by default, especially for devices with a notch
			//if (window.AndroidNotch) {
			//	this.setAndroidSafeArea();
			//}

			// Init translations
			this.i18nService.initTranslate().then(() => {
				// App is opened by URL
				if (Capacitor.isPluginAvailable('App')) {
					App.addListener('appUrlOpen', (data: URLOpenListenerEvent) => this.handleAppLink(data.url));
				}
			});
		});
	}

	private handleKeyboardToggle(): void {
		Keyboard.addListener('keyboardDidShow', keyboardInfo => {
			const style = document.documentElement.querySelector('ion-app')?.style;
			style?.setProperty('margin-bottom', `${keyboardInfo.keyboardHeight}px`);
		});
		Keyboard.addListener('keyboardWillHide', () => {
			const style = document.documentElement.querySelector('ion-app')?.style;
			style?.setProperty('margin-bottom', '0px');
		});
	}

	// private setAndroidSafeArea(): void {
	// 	const minStatusBarHeight = 25;
	// 	const minNavigationBarHeight = 50;
	// 	const style = document.documentElement.style;
	// 	window.AndroidNotch.getInsetTop(px => {
	// 		style.setProperty(
	// 			'--ion-safe-area-top',
	// 			`${Math.max(minStatusBarHeight, +px)}px`
	// 		);
	// 	});
	// 	window.AndroidNotch.getInsetBottom(px =>
	// 		style.setProperty(
	// 			'--ion-safe-area-bottom',
	// 			`${Math.max(minNavigationBarHeight, +px)}px`
	// 		)
	// 	);
	// 	window.AndroidNotch.getInsetLeft(px =>
	// 		style.setProperty('--ion-safe-area-left', `${px as string}px`)
	// 	);
	// 	window.AndroidNotch.getInsetRight(px =>
	// 		style.setProperty('--ion-safe-area-right', `${px as string}px`)
	// 	);
	// }

	private handleAppLink(link: string): void {
		if (!link) {
			return;
		}

		const url = new URL(link);
		// If this is a Keycloak exchange to get an Access Token by Code, do not handle.
		// Otherwise an additional login process could be started
		const params = new URLSearchParams(url.hash.substring(1));
		const code = params.get('code');

		if (!code && environment.nativeAppLinkHost === url.host && url.pathname) {
			this.router.navigateByUrl(url.pathname + url.search, {
				replaceUrl: true
			});
		}
	}

	private runAppStateChangeListener(): void {
		App.addListener('appStateChange', (state: AppState) => {
			if (state.isActive) {
				// App has become active
				this.i18nService.setLanguageByDevice();
			} else {
				// App has become inactive
			}
		});
	}
}
